import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const ADMIN_DATE_PREFERENCE_KEY = "cps_admin_calendar_year_default";

const readCachedPreference = () => {
  try {
    const value = localStorage.getItem(ADMIN_DATE_PREFERENCE_KEY);
    if (value === "true") return true;
    if (value === "false") return false;
  } catch (_) { /* storage unavailable */ }
  return null;
};

export const cacheAdminDatePreference = (enabled) => {
  try { localStorage.setItem(ADMIN_DATE_PREFERENCE_KEY, enabled ? "true" : "false"); }
  catch (_) { /* storage unavailable */ }
};

export const currentCalendarYearRange = (now = new Date()) => {
  const year = now.getFullYear();
  return { from: `${year}-01-01`, to: `${year}-12-31` };
};

const localDate = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return raw.slice(0, 10);
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
};

export const valueInDateRange = (value, from, to) => {
  if (!from && !to) return true;
  const date = localDate(value);
  if (!date) return false;
  return (!from || date >= from) && (!to || date <= to);
};

export function useAdminDateRange() {
  const year = useMemo(() => currentCalendarYearRange(), []);
  const initialPreference = useMemo(() => readCachedPreference(), []);
  const startsInCurrentYear = initialPreference !== false;
  const [dateFrom, setDateFrom] = useState(startsInCurrentYear ? year.from : "");
  const [dateTo, setDateTo] = useState(startsInCurrentYear ? year.to : "");
  const [preferenceEnabled, setPreferenceEnabled] = useState(startsInCurrentYear);
  const changedByUser = useRef(false);

  useEffect(() => {
    let active = true;
    api.get("/date-filter-settings")
      .then(({ data }) => {
        if (!active) return;
        const enabled = data?.enabled !== false;
        cacheAdminDatePreference(enabled);
        setPreferenceEnabled(enabled);
        if (!changedByUser.current) {
          setDateFrom(enabled ? year.from : "");
          setDateTo(enabled ? year.to : "");
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [year.from, year.to]);

  const changeFrom = useCallback((value) => { changedByUser.current = true; setDateFrom(value); }, []);
  const changeTo = useCallback((value) => { changedByUser.current = true; setDateTo(value); }, []);
  const showAll = useCallback(() => { changedByUser.current = true; setDateFrom(""); setDateTo(""); }, []);
  const showCurrentYear = useCallback(() => { changedByUser.current = true; setDateFrom(year.from); setDateTo(year.to); }, [year.from, year.to]);

  return {
    dateFrom,
    dateTo,
    setDateFrom: changeFrom,
    setDateTo: changeTo,
    preferenceEnabled,
    showAll,
    showCurrentYear,
  };
}